import { ArrowUpIcon, ArrowDownIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { formatCurrencyOrEmptyValue } from 'support/helpers/currency/currency';
import { InvoiceVerificationDTO } from 'support/types';

type InvoiceVerificationDetails = Required<InvoiceVerificationDTO>['payload'];

type DueAmountData = {
  actual?: number | null | undefined;
  expected?: number | null | undefined;
};

type AmountDifference = {
  type: 'excess' | 'discount' | 'none';
  percentage: string | null;
};

const calculateAmountDifference = (dueAmount: DueAmountData): AmountDifference => {
  if (dueAmount?.actual == null || dueAmount?.expected == null || dueAmount.expected <= 0) {
    return { type: 'none', percentage: null };
  }

  if (dueAmount.actual > dueAmount.expected) {
    const percentage = (((dueAmount.actual - dueAmount.expected) / dueAmount.expected) * 100).toFixed(1);
    return { type: 'excess', percentage };
  }

  if (dueAmount.actual < dueAmount.expected) {
    const percentage = (((dueAmount.expected - dueAmount.actual) / dueAmount.expected) * 100).toFixed(1);
    return { type: 'discount', percentage };
  }

  return { type: 'none', percentage: null };
};

const DifferenceIndicator = ({ difference }: { difference: AmountDifference }) => {
  if (difference.type === 'none') {
    return null;
  }

  const isExcess = difference.type === 'excess';
  const Icon = isExcess ? ArrowUpIcon : ArrowDownIcon;
  const colorClass = isExcess ? 'text-red-500' : 'text-green-500';

  return (
    <div className="mr-2 flex items-center">
      <Icon className={`mr-0.5 size-4 ${colorClass}`} />
      <span className="text-xs font-medium text-gray-600">{difference.percentage}%</span>
    </div>
  );
};

export const Footer = ({
  data,
  currency,
  showDifferenceIndicator,
}: {
  data: Partial<InvoiceVerificationDetails['summary']>;
  currency: InvoiceVerificationDetails['currency'];
  showDifferenceIndicator: boolean;
}) => {
  const { t } = useTranslation();

  const dueAmount = data?.due_amount;
  const difference = calculateAmountDifference(dueAmount || {});

  return (
    <tfoot>
      <tr>
        <td className="bg-gray-50 py-3 pl-6 text-gray-900">
          {t('invoiceVerification:detailsPage.summaryData.dueAmount.label')}
        </td>
        <td className="bg-gray-50 py-3 text-right font-semibold tabular-nums">
          {formatCurrencyOrEmptyValue(dueAmount?.expected, currency)}
        </td>
        <td className="bg-gray-50 py-3 pr-6 text-right">
          <div className="flex items-center justify-end">
            {showDifferenceIndicator ? <DifferenceIndicator difference={difference} /> : null}
            <span className="font-semibold tabular-nums">
              {formatCurrencyOrEmptyValue(dueAmount?.actual, currency)}
            </span>
          </div>
        </td>
      </tr>
    </tfoot>
  );
};
